import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { BACKEND_URL } from '../config';

const OrderTracking = () => {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchMode, setSearchMode] = useState(!token);
  const [searchData, setSearchData] = useState({
    order_number: '',
    phone: ''
  });
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (token) {
      loadOrder();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadOrder = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/boutique/track/${token}`);
      const data = await res.json();
      
      if (data.success) {
        setOrder(data.order);
      } else {
        setError(data.message || 'Commande non trouvée');
      }
    } catch (err) {
      console.error(err);
      setError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchData.order_number || !searchData.phone) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    setSearching(true);
    setError(null);
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/boutique/search-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(searchData)
      });
      
      const data = await res.json();
      
      if (data.success) {
        setOrder(data.order);
        setSearchMode(false);
      } else {
        setError(data.message || 'Commande non trouvée');
      }
    } catch (err) {
      console.error(err);
      setError('Erreur lors de la recherche');
    } finally {
      setSearching(false);
    }
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${BACKEND_URL}/${path}`;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusInfo = (status) => {
    const info = {
      pending: {
        icon: '⏳',
        label: 'En attente',
        description: 'Votre commande attend la confirmation du vendeur',
        color: 'amber',
        step: 1
      },
      confirmed: {
        icon: '✓',
        label: 'Confirmée',
        description: 'Votre commande a été confirmée',
        color: 'blue',
        step: 2
      },
      preparing: {
        icon: '🔧',
        label: 'En préparation',
        description: 'Votre commande est en cours de préparation',
        color: 'purple',
        step: 3
      },
      delivered: {
        icon: '✅',
        label: 'Livrée',
        description: 'Votre commande a été livrée',
        color: 'green',
        step: 4
      },
      cancelled: {
        icon: '❌',
        label: 'Annulée',
        description: 'Cette commande a été annulée',
        color: 'red',
        step: 0
      }
    };
    return info[status] || info.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Page de recherche
  if (searchMode || (!order && !error)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">📦</span>
            </div>
            <h1 className="text-xl font-bold text-gray-800">Suivre ma commande</h1>
            <p className="text-gray-500 text-sm mt-2">
              Entrez votre numéro de commande et votre téléphone pour voir l'état de votre commande
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSearch} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Numéro de commande
              </label>
              <input
                type="text"
                value={searchData.order_number}
                onChange={(e) => setSearchData(prev => ({ ...prev, order_number: e.target.value.toUpperCase() }))}
                placeholder="ex: CMD250104ABCD"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Téléphone utilisé lors de la commande
              </label>
              <input
                type="tel"
                value={searchData.phone}
                onChange={(e) => setSearchData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="ex: +221 77 123 45 67"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>

            <button
              type="submit"
              disabled={searching}
              className="w-full py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 transition"
            >
              {searching ? 'Recherche...' : 'Rechercher'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (error && !order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-5xl mb-4">🔍</div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">Commande non trouvée</h1>
          <p className="text-gray-500 mb-6">{error}</p>
          <button
            onClick={() => { setError(null); setSearchMode(true); }}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(order.status);
  const steps = ['Commande passée', 'Confirmée', 'En préparation', 'Livrée'];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <h1 className="text-lg font-bold text-gray-800">Suivi de commande</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Statut principal */}
        <div className={`bg-${statusInfo.color}-50 rounded-xl p-6 text-center`}>
          <div className="text-5xl mb-3">{statusInfo.icon}</div>
          <h2 className={`text-xl font-bold text-${statusInfo.color}-800`}>{statusInfo.label}</h2>
          <p className={`text-${statusInfo.color}-600 mt-1`}>{statusInfo.description}</p>
          
          {order.status === 'cancelled' && order.cancelled_reason && (
            <p className="mt-3 text-sm text-red-600 bg-red-100 p-2 rounded-lg">
              Raison: {order.cancelled_reason}
            </p>
          )}
        </div>

        {/* Timeline des étapes */}
        {order.status !== 'cancelled' && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex justify-between relative">
              {/* Ligne de connexion */}
              <div className="absolute top-4 left-0 right-0 h-1 bg-gray-200" style={{ width: 'calc(100% - 2rem)', marginLeft: '1rem' }}></div>
              <div 
                className="absolute top-4 left-0 h-1 bg-indigo-600 transition-all"
                style={{ 
                  width: `calc(${(statusInfo.step - 1) / 3 * 100}% - ${statusInfo.step > 1 ? '0.5rem' : '0'})`,
                  marginLeft: '1rem'
                }}
              ></div>
              
              {steps.map((step, idx) => (
                <div key={idx} className="flex flex-col items-center relative z-10">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    idx + 1 <= statusInfo.step
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    {idx + 1 <= statusInfo.step ? '✓' : idx + 1}
                  </div>
                  <span className="text-xs text-gray-500 mt-2 text-center max-w-[70px]">{step}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Infos commande */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <div className="text-sm text-gray-500">Numéro de commande</div>
              <div className="font-bold text-gray-800">{order.order_number}</div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">Passée le</div>
              <div className="text-gray-800">{formatDate(order.created_at)}</div>
            </div>
          </div>

          <div className="pt-4 border-t">
            <div className="text-sm text-gray-500 mb-2">Boutique</div>
            <div className="flex items-center gap-3">
              <div className="font-medium text-gray-800">{order.shop_name}</div>
              {order.shop_slug && (
                <Link
                  to={`/boutique/${order.shop_slug}`}
                  className="text-indigo-600 hover:text-indigo-800 text-sm"
                >
                  Voir la boutique →
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Articles */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="font-semibold text-gray-800 mb-4">Articles commandés</h3>
          <div className="space-y-3">
            {order.items?.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 py-2 border-b last:border-0">
                <div className="w-14 h-14 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                  {item.product_image ? (
                    <img src={getImageUrl(item.product_image)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xl">📦</span>
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-800">{item.product_name}</div>
                  <div className="text-sm text-gray-500">
                    {parseInt(item.product_price).toLocaleString()} × {item.quantity}
                  </div>
                </div>
                <div className="font-medium text-gray-800">
                  {parseInt(item.subtotal).toLocaleString()} {order.currency}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Récap paiement */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="font-semibold text-gray-800 mb-4">Récapitulatif</h3>
          <div className="space-y-2">
            <div className="flex justify-between text-gray-600">
              <span>Sous-total</span>
              <span>{parseInt(order.subtotal).toLocaleString()} {order.currency}</span>
            </div>
            {parseFloat(order.delivery_fee) > 0 && (
              <div className="flex justify-between text-gray-600">
                <span>Frais de livraison</span>
                <span>{parseInt(order.delivery_fee).toLocaleString()} {order.currency}</span>
              </div>
            )}
            <div className="flex justify-between text-xl font-bold text-gray-800 pt-2 border-t">
              <span>Total</span>
              <span>{parseInt(order.total_amount).toLocaleString()} {order.currency}</span>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>{order.delivery_type === 'delivery' ? '🚚 Livraison' : '🏠 Retrait sur place'}</span>
              {order.delivery_type === 'delivery' && order.customer_address && (
                <span className="text-gray-400">• {order.customer_address}</span>
              )}
            </div>
          </div>
        </div>

        {/* Contact boutique */}
        {(order.shop_phone || order.shop_whatsapp) && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-semibold text-gray-800 mb-4">Contacter la boutique</h3>
            <div className="flex flex-wrap gap-3">
              {order.shop_phone && (
                <a
                  href={`tel:${order.shop_phone}`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                >
                  📞 Appeler
                </a>
              )}
              {order.shop_whatsapp && (
                <a
                  href={`https://wa.me/${order.shop_whatsapp.replace(/[^0-9]/g, '')}?text=Bonjour, concernant ma commande ${order.order_number}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
                >
                  💬 WhatsApp
                </a>
              )}
            </div>
          </div>
        )}

        {/* Notes */}
        {order.notes && (
          <div className="bg-amber-50 rounded-xl p-4">
            <h3 className="font-medium text-amber-800 mb-2">📝 Vos notes</h3>
            <p className="text-sm text-amber-700">{order.notes}</p>
          </div>
        )}

        {/* Historique */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="font-semibold text-gray-800 mb-4">📅 Historique</h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-gray-400 rounded-full mt-2"></div>
              <div>
                <div className="font-medium text-gray-800">Commande passée</div>
                <div className="text-sm text-gray-500">{formatDate(order.created_at)}</div>
              </div>
            </div>
            {order.confirmed_at && (
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <div>
                  <div className="font-medium text-gray-800">Confirmée</div>
                  <div className="text-sm text-gray-500">{formatDate(order.confirmed_at)}</div>
                </div>
              </div>
            )}
            {order.preparing_at && (
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                <div>
                  <div className="font-medium text-gray-800">En préparation</div>
                  <div className="text-sm text-gray-500">{formatDate(order.preparing_at)}</div>
                </div>
              </div>
            )}
            {order.delivered_at && (
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                <div>
                  <div className="font-medium text-gray-800">Livrée</div>
                  <div className="text-sm text-gray-500">{formatDate(order.delivered_at)}</div>
                </div>
              </div>
            )}
            {order.cancelled_at && (
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                <div>
                  <div className="font-medium text-gray-800">Annulée</div>
                  <div className="text-sm text-gray-500">{formatDate(order.cancelled_at)}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center py-4 text-sm text-gray-400">
          Propulsé par DJAMANN
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;
